import { type SignInInput, useAuth } from '@drycut/auth';
import { ControlledTextField } from '@drycut/controlled-components';
import {
  Button,
  CenteredSquaredContainer,
  FormHelpText,
  FullscreenImageLayout,
  Heading,
  MobileNavbar,
  Para,
  Stack,
} from '@drycut/ui-components';
import { ShieldExclamationIcon } from '@heroicons/react/20/solid';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useDocumentTitle } from 'usehooks-ts';

import BuildVersionInfo from '../BuildVersionInfo';

export function Login(): React.ReactElement {
  useDocumentTitle('DRYCUT - Stylist dashboard');

  const { signIn, error, loading } = useAuth();
  const navigate = useNavigate();

  const formProps = useForm<SignInInput>({
    defaultValues: {
      slug: '',
      password: '',
    },
  });

  const onSubmit = async (input: SignInInput) => {
    const loggedIn = await signIn(input);

    if (loggedIn) {
      navigate('/dashboard');
    }
  };

  return (
    <FullscreenImageLayout src="/images/background.jpg">
      <CenteredSquaredContainer>
        <MobileNavbar title="Dashboard" />
        <Stack gap={3} className="text-center">
          <Heading variant="h2">Log in.</Heading>
          <Para pretty>Enter the store credentials to access the system.</Para>
        </Stack>

        <FormProvider {...formProps}>
          <form onSubmit={formProps.handleSubmit(onSubmit)}>
            <fieldset disabled={loading} className="flex flex-col gap-4 my-10">
              <ControlledTextField label="Store" name="slug" autoComplete="off" required />
              <ControlledTextField label="Password" name="password" type="password" required />

              <FormHelpText error>
                {error && (
                  <Stack direction="row" gap={1} className="grow items-center justify-center">
                    <ShieldExclamationIcon className="w-5 h-5" />
                    Email oder Passwort ungültig.
                    <span className="hidden" aria-hidden>
                      {error}
                    </span>
                  </Stack>
                )}
              </FormHelpText>
            </fieldset>

            <Button type="submit" loading={loading} className="w-full">
              Log in
            </Button>
          </form>
        </FormProvider>
      </CenteredSquaredContainer>
      <BuildVersionInfo />
    </FullscreenImageLayout>
  );
}
